<script setup lang="ts" generic="T extends string | number">
import {
  SelectContent,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from 'reka-ui'

export interface SelectOption<T> {
  value: T
  label: string
  disabled?: boolean
}

withDefaults(
  defineProps<{
    modelValue: T | undefined
    options: readonly SelectOption<T>[]
    placeholder?: string
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; an empty aria-label suppresses the accessible name
    ariaLabel?: string
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; an empty id is a real attribute that can collide
    id?: string
    disabled?: boolean
    variant?: 'field' | 'icon' | 'prominent' | 'chip'
  }>(),
  { variant: 'field', placeholder: '', disabled: false },
)

const emit = defineEmits<{ 'update:modelValue': [value: T] }>()

const TRIGGER_CLASS = {
  field:
    'flex h-[var(--density-control)] min-w-[var(--reka-select-trigger-width)] items-center justify-between gap-[var(--space-inline)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] outline-none data-[state=open]:border-[color:var(--color-focus)] data-[placeholder]:text-[color:var(--color-text-muted)]',
  icon: 'flex h-[var(--density-control)] w-[var(--density-control)] items-center justify-center rounded-[var(--radius-control)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)] outline-none transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-raised)] hover:text-[color:var(--color-text)] data-[state=open]:bg-[color:var(--color-surface-raised)] data-[state=open]:text-[color:var(--color-text)]',
  prominent:
    'flex h-[var(--density-control)] max-w-64 items-center gap-[var(--space-inline)] truncate rounded-[var(--radius-control)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] pl-[var(--space-cell)] pr-[var(--space-inline)] text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)] outline-none transition-colors duration-[var(--motion-fast)] hover:border-[color:var(--color-border-control)] data-[state=open]:border-[color:var(--color-focus)]',
  chip: 'flex h-full items-center gap-[var(--space-tight)] rounded-[var(--radius-control)] pl-[var(--space-cell)] pr-[var(--space-inline)] text-[length:var(--text-step-2)]',
} as const
</script>

<template>
  <SelectRoot
    v-bind="modelValue === undefined ? {} : { modelValue }"
    :disabled="disabled"
    @update:model-value="(value: T) => emit('update:modelValue', value)"
  >
    <SelectTrigger :id="id" :aria-label="ariaLabel" :class="TRIGGER_CLASS[variant]">
      <slot name="trigger">
        <SelectValue class="truncate" :placeholder="placeholder" />
        <svg
          v-if="variant !== 'icon'"
          width="14"
          height="14"
          viewBox="0 0 16 16"
          aria-hidden="true"
          class="shrink-0 text-[color:var(--color-text-muted)]"
        >
          <path
            d="M4 6.5 8 10.5 12 6.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </slot>
    </SelectTrigger>
    <SelectPortal>
      <SelectContent
        position="popper"
        side="bottom"
        align="start"
        :side-offset="4"
        class="z-40 min-w-[var(--reka-select-trigger-width)] overflow-hidden rounded-[var(--radius-overlay)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] shadow-[var(--shadow-overlay)]"
      >
        <SelectViewport class="max-h-72 overflow-y-auto p-[var(--space-tight)]">
          <SelectItem
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            :disabled="option.disabled ?? false"
            class="flex min-h-[var(--density-target-min)] cursor-pointer select-none items-center justify-between gap-[var(--space-cell)] rounded-[var(--radius-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text)] outline-none data-[highlighted]:bg-[color:var(--color-surface-raised)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
          >
            <SelectItemText>{{ option.label }}</SelectItemText>
            <SelectItemIndicator class="shrink-0 text-[color:var(--color-accent)]">
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M3 8.4 6.4 11.8 13 5.2"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </SelectItemIndicator>
          </SelectItem>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
